// Verbatim copy used by the invite-members results UI. Centralized here so
// ticket-mandated strings live in exactly one place and cannot drift between
// the InviteOfficeMates screen and the onboarding AddMembers screen.
//
// The reason codes below MUST stay in lockstep with the backend constants in
// backend/utils/messages.js (INVITE_REASON_*). The backend returns
// errorRes: [{ email, reason }] from POST /users/v1/add-members, and this
// module turns each entry into a human-readable sentence.

import { buildAlreadyInvitedMessage } from './constants';

export const REASON_ALREADY_INVITED = 'already-invited';
export const REASON_INVALID_EMAIL = 'invalid-email';
export const REASON_DUPLICATE_IN_PAYLOAD = 'duplicate-in-payload';
export const REASON_INSERT_ERROR = 'insert-error';

// Shared suffix reused by the already-invited reason and the duplicate-mobile
// message so the wording lives in exactly one place.
export const ALREADY_INVITED_SUFFIX = 'has been invited before and can not be re-invited.';

// Message shown when a mobile number is rejected by the backend as a duplicate.
export const DUPLICATE_MOBILE_MESSAGE = `This mobile number ${ALREADY_INVITED_SUFFIX}`;

// Map of reason code -> human sentence fragment describing why the invite for
// that email failed. An unmapped/unknown reason degrades to a generic line so
// the UI never renders a raw code or a blank line.
const REASON_SENTENCES = {
  [REASON_ALREADY_INVITED]: ALREADY_INVITED_SUFFIX,
  [REASON_INVALID_EMAIL]: 'is not a valid email and could not be invited.',
  [REASON_DUPLICATE_IN_PAYLOAD]: 'was entered more than once and could not be invited.',
  [REASON_INSERT_ERROR]: 'could not be invited due to an unexpected error. Please try again.',
};

const GENERIC_REASON_SENTENCE = 'could not be invited.';

// Builds the green success line, e.g. "3 users successfully invited." /
// "1 user successfully invited." Preserves singular/plural wording.
export const buildSuccessCountLine = (count) => {
  const noun = count === 1 ? 'user' : 'users';
  return `${count} ${noun} successfully invited.`;
};

// Builds the green success line, NAMING the invited emails so the admin sees
// WHICH users went through (mirrors the failure line — the ticket asks to show
// which ones succeeded, not just how many). A large batch would be unwieldy, so
// past SUCCESS_NAME_LIMIT it falls back to the plain count.
const SUCCESS_NAME_LIMIT = 5;
export const buildSuccessMessage = (successRes) => {
  if (!successRes || !successRes.length) {
    return '';
  }
  if (successRes.length > SUCCESS_NAME_LIMIT) {
    return buildSuccessCountLine(successRes.length);
  }
  const emails = successRes.map((entry) => (entry && entry.email ? entry.email : 'A user'));
  const verb = emails.length === 1 ? 'was' : 'were';
  return `${emails.join(', ')} ${verb} successfully invited.`;
};

// Returns the human sentence for a single reason code, falling back to a
// generic sentence for any unknown/unmapped/empty code.
export const reasonToSentence = (reason) => {
  if (reason && REASON_SENTENCES[reason]) {
    return REASON_SENTENCES[reason];
  }
  return GENERIC_REASON_SENTENCE;
};

// Turns the backend errorRes array ([{ email, reason }]) into a single
// displayable failure message with one "email reason" sentence per failure.
// Entries missing an email are still rendered with a generic subject so a
// failure is never silently dropped. Returns '' when there are no failures.
export const buildFailureMessage = (errorRes) => {
  if (!errorRes || !errorRes.length) {
    return '';
  }
  // "already-invited" failures are consolidated into ONE sentence via
  // buildAlreadyInvitedMessage (the Users-List wording, incl. the "this account
  // or another account" phrasing) instead of repeating a sentence per email.
  // Every other reason is still rendered as one short sentence per email.
  const alreadyInvitedEmails = [];
  const otherLines = [];
  errorRes.forEach((entry) => {
    const subject = entry && entry.email ? entry.email : 'A user';
    if (entry && entry.reason === REASON_ALREADY_INVITED) {
      alreadyInvitedEmails.push(subject);
    } else {
      otherLines.push(`${subject} ${reasonToSentence(entry && entry.reason)}`);
    }
  });
  const parts = [];
  if (alreadyInvitedEmails.length) {
    parts.push(buildAlreadyInvitedMessage(alreadyInvitedEmails));
  }
  if (otherLines.length) {
    parts.push(otherLines.join(' '));
  }
  return parts.join(' ');
};
